<template>
  <section class="client-cabinet-plain-section" data-testid="client-page-breakdown" data-field="booking.technical" aria-labelledby="client-page-breakdown-title">
    <h2 id="client-page-breakdown-title">{{ $t("client_page.cabinet.breakdown.title") }}</h2>
    <div class="client-cabinet-summary-grid" data-testid="client-page-breakdown-summary">
      <span>{{ $t("client_page.cabinet.breakdown.total") }}</span>
      <strong>{{ m(cost) }}</strong>
      <template v-if="hasDue">
        <span>{{ $t("client_page.cabinet.breakdown.paid") }}</span>
        <strong>{{ m(paid) }}</strong>
        <span>{{ $t("client_page.cabinet.breakdown.due") }}</span>
        <strong :class="{ 'client-cabinet-due': due > 0 }">{{ m(due) }}</strong>
      </template>
    </div>
    <button
      v-if="!booking.technical"
      type="button"
      class="client-cabinet-disclosure"
      data-testid="client-page-disclosure-breakdown"
      :aria-expanded="String(showBreakdown)"
      aria-controls="client-page-breakdown-content"
      @click="showBreakdown = !showBreakdown"
    >
      {{ $t(showBreakdown ? "client_page.cabinet.breakdown.hide" : "client_page.cabinet.breakdown.show") }}
    </button>
    <div id="client-page-breakdown-content" v-show="showBreakdown" data-testid="client-page-breakdown-content">
      <v-simple-table v-if="!booking.technical" dense>
        <template v-slot:default>
          <!-- Строки — начисления Fleet::BookingMoney::CHARGE_COLUMNS (та же формула, что «К оплате» сервера) и залог. -->
          <tbody
            data-field="booking.price booking.sale booking.sale_cash booking.aggr_commission booking.rental_cost booking.delivery booking.delivery_end booking.equipment booking.addons_cost booking.insurance booking.clean_payment booking.add_drivers_cost booking.hours_cost booking.mileage_cost booking.damage booking.gas booking.fine booking.other booking.other_end booking.hours_cost_end booking.deposit booking.hourly_deposit booking.weekly_deposit booking.monthly_deposit booking.hourly booking.weekly booking.monthly booking.ride"
          >
            <tr v-if="booking.price > 0">
              <td>{{ $t(priceLabel) }}</td>
              <td class="text-right">{{ m(booking.price) }}</td>
            </tr>
            <tr v-if="booking.rental_cost > 0">
              <td>{{ $t("bookings.rental_cost") }}</td>
              <td class="text-right">{{ m(booking.rental_cost) }}</td>
            </tr>
            <tr v-if="booking.sale_cash > 0">
              <td>{{ $t("bookings.sale") }}</td>
              <td class="text-right">{{ m(booking.sale_cash) }}</td>
            </tr>
            <tr v-if="booking.sale > 0">
              <td>{{ $t("bookings.sale") }}</td>
              <td class="text-right">{{ booking.sale }}%</td>
            </tr>
            <tr v-if="booking.hours_cost > 0">
              <td>{{ $t("bookings.hours_cost") }}</td>
              <td class="text-right">{{ m(booking.hours_cost) }}</td>
            </tr>
            <tr v-if="booking.hours_cost_end > 0">
              <td>{{ $t("bookings.hours_cost_end") }}</td>
              <td class="text-right">{{ m(booking.hours_cost_end) }}</td>
            </tr>
            <tr v-if="booking.delivery > 0">
              <td>{{ $t("bookings.delivery") }}</td>
              <td class="text-right">{{ m(booking.delivery) }}</td>
            </tr>
            <tr v-if="booking.delivery_end > 0">
              <td>{{ $t("bookings.checkout") }}</td>
              <td class="text-right">{{ m(booking.delivery_end) }}</td>
            </tr>
            <!-- UAT 2026-08-07: рядом с каталожными допами FT-102 легаси-сумма называется «Другое оборудование». -->
            <tr v-if="booking.equipment > 0">
              <td>{{ $t("bookings.other_equipment") }}</td>
              <td class="text-right">{{ m(booking.equipment) }}</td>
            </tr>
            <tr v-if="booking.addons_cost > 0">
              <td>{{ $t("booking_addons.title") }}</td>
              <td class="text-right">{{ m(booking.addons_cost) }}</td>
            </tr>
            <tr v-if="booking.clean_payment > 0">
              <td>{{ $t("bookings.clean") }}</td>
              <td class="text-right">{{ m(booking.clean_payment) }}</td>
            </tr>
            <tr v-if="booking.damage > 0">
              <td>{{ $t("bookings.damages") }}</td>
              <td class="text-right">{{ m(booking.damage) }}</td>
            </tr>
            <tr v-if="booking.gas > 0">
              <td>{{ $t("bookings.for_gas") }}</td>
              <td class="text-right">{{ m(booking.gas) }}</td>
            </tr>
            <tr v-if="booking.add_drivers_cost > 0">
              <td>{{ $t("bookings.for_add_drivers") }}</td>
              <td class="text-right">{{ m(booking.add_drivers_cost) }}</td>
            </tr>
            <tr v-if="booking.insurance > 0">
              <td>{{ $t("bookings.for_add_insurance") }}</td>
              <td class="text-right">{{ m(booking.insurance) }}</td>
            </tr>
            <tr v-if="booking.fine > 0">
              <td>{{ $t("bookings.for_fines") }}</td>
              <td class="text-right">{{ m(booking.fine) }}</td>
            </tr>
            <tr v-if="booking.other > 0">
              <td>{{ $t("bookings.for_other") }}</td>
              <td class="text-right">{{ m(booking.other) }}</td>
            </tr>
            <tr v-if="booking.other_end > 0">
              <td>{{ $t("bookings.for_other_end") }}</td>
              <td class="text-right">{{ m(booking.other_end) }}</td>
            </tr>
            <tr v-if="booking.mileage_cost > 0">
              <td>{{ $t("bookings.mileage_cost") }}</td>
              <td class="text-right">{{ m(booking.mileage_cost) }}</td>
            </tr>
            <tr v-if="booking.aggr_commission > 0">
              <td>{{ $t("bookings.aggr_commission") }}</td>
              <td class="text-right">{{ m(booking.aggr_commission) }}</td>
            </tr>
            <!-- Залог в счёте, пока машина не возвращена (ride) — как в Fleet::BookingMoney.cost. -->
            <tr v-if="deposit > 0 && !booking.ride">
              <td>{{ $t("bookings.deposit") }}</td>
              <td class="text-right">{{ m(deposit) }}</td>
            </tr>
            <tr
              v-for="(gift, index) in booking.gifts || []"
              :key="`gift-${index}`"
              data-field="booking.gifts[].gift_type booking.gifts[].count booking.gifts[].price booking.gifts[].created_at"
            >
              <td colspan="2">{{ $t("client_page.cabinet.breakdown.gift", { period: giftPeriod(gift), amount: m(gift.price), date: formatDate(gift.created_at) }) }}</td>
            </tr>
            <tr class="font-weight-bold">
              <td>{{ $t("custom.total") }}</td>
              <td class="text-right" data-testid="client-page-breakdown-total">{{ m(cost) }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>
  </section>
</template>

<script>
// FT-200 STEP-05 (REQ-08): свёрнутое «Из чего складывается сумма» — начисления бронирования, залог, подарки и итог.
// Итог — та же формула, что «К оплате» сервера (Fleet::BookingMoney.cost: начисления + залог, пока машина не
// возвращена); строк записанных ненулевых сумм флаги FT-183 не скрывают.
import moment from "moment";
import { bookingCost, cabinetDebtAmount, currentDeposit, money, roundMoney } from "@/utils/clientCabinetView";

export default {
  name: "AmountBreakdown",
  props: {
    booking: { type: Object, required: true },
    company: { type: Object, default: null },
    client: { type: Object, default: null },
    toPay: { type: [String, Number], default: null },
  },
  data() {
    return { showBreakdown: false };
  },
  computed: {
    deposit() {
      return currentDeposit(this.booking);
    },
    cost() {
      return bookingCost(this.booking);
    },
    due() {
      return cabinetDebtAmount(this.booking, this.toPay, this.client && this.client.balance);
    },
    hasDue() {
      return !Number.isNaN(this.due);
    },
    paid() {
      return roundMoney(Math.max(this.cost - this.due, 0));
    },
    priceLabel() {
      if (this.booking.hourly) return "client_page.cabinet.breakdown.price_hour";
      if (this.booking.weekly) return "client_page.cabinet.breakdown.price_week";
      if (this.booking.monthly) return "client_page.cabinet.breakdown.price_month";
      return "client_page.cabinet.breakdown.price_day";
    },
  },
  methods: {
    m(value) {
      return money(value, this.company && this.company.currency_symbol);
    },
    // Подарок — бесплатные сутки (gift_type 0) или часы.
    giftPeriod(gift) {
      return gift.gift_type == 0
        ? this.$t("client_page.cabinet.duration_days", { count: gift.count })
        : this.$t("client_page.cabinet.duration_hours", { count: gift.count });
    },
    formatDate(date) {
      return date ? moment(date).locale((this.$i18n && this.$i18n.locale) || "ru").format("D MMMM") : "";
    },
  },
};
</script>
